class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    
    // Update shipping progress bars
    this.updateShippingProgress();
    
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  updateShippingProgress() {
  const progressContainer = document.getElementById('CartShippingProgress');
  if (!progressContainer) return;

  // Get all cart items and group by vendor
  const cartItems = document.querySelectorAll('.cart-item[data-vendor]');
  const vendorCounts = {};
  const EXCLUDED_VENDORS = ['gocoffeego'];
  const FREE_SHIPPING_THRESHOLD = 5;

  cartItems.forEach(item => {
    const vendor = item.getAttribute('data-vendor');
    if (EXCLUDED_VENDORS.includes(vendor?.toLowerCase())) return;
    if (vendor) {
      const quantityInput = item.querySelector('.quantity__input');
      const quantity = quantityInput ? parseInt(quantityInput.value) || 0 : 0;
      if (quantity > 0) {
        vendorCounts[vendor] = (vendorCounts[vendor] || 0) + quantity;
      }
    }
  });

  // If no vendors, hide and exit
  if (Object.keys(vendorCounts).length === 0) {
    progressContainer.style.display = 'none';
    return;
  }

  // Split vendors into earned and not yet earned
  const earned = [];
  const notYet = [];

  Object.entries(vendorCounts).forEach(([vendor, count]) => {
    if (count >= FREE_SHIPPING_THRESHOLD) {
      earned.push(vendor);
    } else {
      notYet.push({ vendor, remaining: FREE_SHIPPING_THRESHOLD - count });
    }
  });

  // Build a single consolidated message
  let message = '';

  if (earned.length > 0 && notYet.length > 0) {
    // Some earned, some not yet
    const earnedStr = earned.join(', ');
    const nextUp = notYet[0]; // Show only the closest one to avoid long messages
    message = `🎉 Free shipping on ${earnedStr}! Add ${nextUp.remaining} more ${nextUp.vendor} product${nextUp.remaining !== 1 ? 's' : ''} to unlock free shipping there too.`;

  } else if (earned.length > 0) {
    // All earned
    const earnedStr = earned.join(' & ');
    message = `🎉 You've earned FREE shipping on ${earnedStr}!`;

  } else {
    // None earned yet — show the one closest to threshold
    notYet.sort((a, b) => a.remaining - b.remaining);
    const nextUp = notYet[0];
    message = `Add ${nextUp.remaining} more ${nextUp.vendor} product${nextUp.remaining !== 1 ? 's' : ''} for FREE shipping!`;
  }

  progressContainer.innerHTML = `
    <div class="shipping-progress-item">
      <div class="shipping-progress-message ${earned.length > 0 ? 'complete' : ''}">
        ${message}
      </div>
    </div>
  `;

  progressContainer.style.display = 'block';
}

  generateProgressSteps(currentCount, threshold) {
    let stepsHTML = '';
    
    for (let i = 1; i <= threshold; i++) {
      const isActive = i === currentCount;
      const isComplete = i < currentCount || currentCount >= threshold;
      const stepClass = isComplete ? 'complete' : (isActive ? 'active' : '');
      const lineClass = isComplete ? 'complete' : (i < currentCount ? 'active' : '');

      // Coffee mug SVG with number
      const coffeeMugSVG = `
        <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
          <!-- Steam lines -->
          <path class="steam" d="M 35 15 Q 35 8 35 5" fill="none" stroke-width="2" stroke-linecap="round"/>
          <path class="steam" d="M 50 12 Q 50 5 50 2" fill="none" stroke-width="2" stroke-linecap="round"/>
          <path class="steam" d="M 65 15 Q 65 8 65 5" fill="none" stroke-width="2" stroke-linecap="round"/>
          
          <!-- Mug body -->
          <path class="mug-body" d="M 20 30 L 20 75 Q 20 85 30 85 L 70 85 Q 80 85 80 75 L 80 30 Z"/>
          
          <!-- Coffee fill (raised from 45 to 35) -->
          <path class="coffee-fill" d="M 22 35 L 22 75 Q 22 83 30 83 L 70 83 Q 78 83 78 75 L 78 35 Z"/>
          
          <!-- Mug handle -->
          <path class="mug-handle" d="M 80 40 Q 95 40 95 55 Q 95 70 80 70" stroke-linecap="round"/>
          
          <!-- Number inside mug -->
          <text class="mug-number" x="50" y="65" text-anchor="middle" font-size="28" font-weight="bold">${i}</text>
        </svg>
      `;


      stepsHTML += `
        <div class="progress-step">
          <div class="progress-circle ${stepClass}">
            ${coffeeMugSVG}
          </div>
          <div class="progress-line ${lineClass}"></div>
        </div>
      `;
    }
    
    return stepsHTML; 
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    
    // Store current progress bar HTML before updating
    const progressContainer = document.getElementById('CartShippingProgress');
    const savedProgressHTML = progressContainer ? progressContainer.innerHTML : '';
    
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      
      // Restore and update the progress bar
      const newProgressContainer = document.getElementById('CartShippingProgress');
      if (newProgressContainer && savedProgressHTML) {
        newProgressContainer.innerHTML = savedProgressHTML;
      }
      this.updateShippingProgress();
      
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

   connectedCallback() {
    super.connectedCallback();
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) cartDrawer.updateShippingProgress();
      }
    });
  }
} 

customElements.define('cart-drawer-items', CartDrawerItems);

